/**
 * Level focus API client — the student's own read/write of the syllabus they
 * want practice to serve right now (`GET`/`PATCH /practice/focus-level`).
 *
 * This is focus, not profile truth. `ownLevelStream` remains the student's
 * enrolled profile pair; `focusLevelStream` is either another active-class
 * cohort they may practise or `null`, which means their own pair. The server
 * re-derives the allowed options for every write and every serve path.
 *
 * Self-only by construction: neither call accepts a user id. In particular,
 * level focus never becomes a client-supplied level override on read routes.
 */
import { fetchWithConfig } from './apiUtils';

export interface LevelStreamPair {
  orgLevel: string;
  examLevel: string | null;
}

export interface LevelFocusResponse {
  /** Stored, validated focus. `null` = the student's own profile pair. */
  focusLevelStream: LevelStreamPair | null;
  /** Active-class-derived options, with the student's own pair first. */
  levelStreamOptions: LevelStreamPair[];
  /** The student's profile pair; this remains the diagnostic/prior cohort. */
  ownLevelStream: LevelStreamPair | null;
}

export interface LevelFocusUpdateResponse {
  /** Accepted stored value. Picking the own pair is normalized to `null`. */
  focusLevelStream: LevelStreamPair | null;
}

const ENDPOINT = '/practice/focus-level';

export const levelFocusApi = {
  async get(token: string): Promise<LevelFocusResponse> {
    return fetchWithConfig<LevelFocusResponse>(ENDPOINT, {
      method: 'GET',
      authToken: token,
    });
  },

  /**
   * Writes the actor's own focus. `422 level_not_allowed` means active class
   * membership changed behind the client, so callers refresh this record.
   * The key is always present: `null` deliberately means the own pair.
   */
  async update(
    levelStream: LevelStreamPair | null,
    token: string
  ): Promise<LevelFocusUpdateResponse> {
    return fetchWithConfig<LevelFocusUpdateResponse>(ENDPOINT, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify({ levelStream }),
    });
  },
};
